import React, { useMemo } from 'react';
import { Autocomplete, Box, Chip, TextField, Typography } from '@mui/material';
import type { JobCode } from 'core/API/scheduler';
import { getBestContrastTextColor } from 'core/utils/colorContrast';
import { parseJobCodeId } from 'module/schedule/utils/jobCodeEligibility';

export const sortJobCodes = (jobCodes: JobCode[]): JobCode[] =>
	[...jobCodes].sort((a, b) => {
		const aNum = a.jobNumber ?? Infinity;
		const bNum = b.jobNumber ?? Infinity;
		if (aNum !== bNum) return aNum - bNum;
		return (a.label || '').localeCompare(b.label || '');
	});

export const formatJobCodeLabel = (jobCode: JobCode): string => {
	const number = jobCode.jobNumber != null ? String(jobCode.jobNumber).padStart(2, '0') : null;
	return number ? `${number} ${jobCode.label}` : jobCode.label;
};

interface EligibleJobCodesPickerProps {
	jobCodes: JobCode[];
	selectedIds: Set<number> | number[];
	onChange: (nextIds: number[]) => void;
	disabled?: boolean;
	loading?: boolean;
	helperText?: string;
	label?: string;
}

/**
 * Compact multi-select for eligible job codes: searchable field with colored chips
 * instead of a tall vertical checkbox list.
 */
const EligibleJobCodesPicker: React.FC<EligibleJobCodesPickerProps> = ({
	jobCodes,
	selectedIds,
	onChange,
	disabled = false,
	loading = false,
	helperText = 'Select one or more Job Codes. At least one is required to appear on the schedule.',
	label = 'Eligible Job Codes',
}) => {
	const sortedJobCodes = useMemo(() => sortJobCodes(jobCodes), [jobCodes]);

	const selectedIdSet = useMemo(() => {
		if (selectedIds instanceof Set) return selectedIds;
		return new Set(selectedIds);
	}, [selectedIds]);

	const selectedJobCodes = useMemo(
		() =>
			sortedJobCodes.filter((jc) => {
				const id = parseJobCodeId(jc) ?? jc.id;
				return selectedIdSet.has(id);
			}),
		[sortedJobCodes, selectedIdSet],
	);

	if (!loading && sortedJobCodes.length === 0) {
		return (
			<Box>
				<Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 600 }}>
					{label}
				</Typography>
				<Typography variant="body2" color="text.secondary">
					No Job Codes are configured yet.
				</Typography>
			</Box>
		);
	}

	return (
		<Box>
			<Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 600 }}>
				{label}
			</Typography>
			{helperText && (
				<Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
					{helperText}
				</Typography>
			)}
			<Autocomplete
				multiple
				disableCloseOnSelect
				options={sortedJobCodes}
				loading={loading}
				disabled={disabled}
				value={selectedJobCodes}
				isOptionEqualToValue={(option, value) => option.id === value.id}
				getOptionLabel={(option) => formatJobCodeLabel(option)}
				filterSelectedOptions={false}
				onChange={(_event, value) => {
					onChange(
						value
							.map((jc) => parseJobCodeId(jc) ?? jc.id)
							.filter((id): id is number => typeof id === 'number' && Number.isFinite(id)),
					);
				}}
				renderOption={(props, option, { selected }) => {
					const { key, ...optionProps } = props as React.HTMLAttributes<HTMLLIElement> & {
						key?: React.Key;
					};
					return (
						<li key={key ?? option.id} {...optionProps}>
							<Box
								sx={{
									width: 12,
									height: 12,
									borderRadius: '50%',
									mr: 1.25,
									flexShrink: 0,
									backgroundColor: option.backgroundColor || '#ccc',
									border: '1px solid rgba(0,0,0,0.12)',
								}}
							/>
							<Box component="span" sx={{ fontWeight: selected ? 600 : 400 }}>
								{formatJobCodeLabel(option)}
							</Box>
						</li>
					);
				}}
				renderTags={(value, getTagProps) =>
					value.map((option, index) => {
						const { key, ...tagProps } = getTagProps({ index });
						const bg = option.backgroundColor || 'rgb(68, 99, 136)';
						// Ignore stored textColor (often black); pick readable black/white from background.
						const fg = getBestContrastTextColor(bg);
						return (
							<Chip
								key={key}
								size="small"
								label={formatJobCodeLabel(option)}
								{...tagProps}
								sx={{
									backgroundColor: bg,
									color: fg,
									fontWeight: 500,
									'& .MuiChip-deleteIcon': {
										color: fg,
										opacity: 0.8,
										'&:hover': { opacity: 1, color: fg },
									},
								}}
							/>
						);
					})
				}
				renderInput={(params) => (
					<TextField
						{...params}
						placeholder={selectedJobCodes.length === 0 ? 'Search and select job codes' : ''}
						helperText={
							selectedJobCodes.length > 0
								? `${selectedJobCodes.length} selected`
								: undefined
						}
					/>
				)}
				sx={{
					'& .MuiAutocomplete-inputRoot': {
						alignItems: 'flex-start',
						py: 1,
					},
				}}
			/>
		</Box>
	);
};

export default EligibleJobCodesPicker;
